import { userApi } from '@shared/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';
import { USER_QUERY_KEY, useSession } from '@/hooks/use-session';
import { SettingsSection } from '../components/settings-section';

export const ProfileTab = () => {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [saved, setSaved] = React.useState(false);

  const mutation = useMutation({
    mutationFn: userApi.updateCurrentUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: captureError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setSaved(false);

    const name = String(new FormData(event.currentTarget).get('name')).trim();
    mutation.mutate({ name: name || null });
  };

  return (
    <SettingsSection
      title="Profile"
      description="How you appear to others in your organization."
      onSubmit={handleSubmit}
      footer={
        <>
          {saved ? (
            <span className="mr-auto text-sm text-positive">Saved</span>
          ) : null}
          <Button type="submit" loading={mutation.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Field label="Name" htmlFor="name">
          <Input
            name="name"
            defaultValue={user?.name ?? ''}
            placeholder="Alex Chen"
          />
        </Field>

        <Field
          label="Email"
          htmlFor="email"
          hint="Changing your email means re-verifying it, which is not supported yet."
        >
          <Input
            name="email"
            value={user?.email ?? ''}
            readOnly
            disabled
            className="bg-surface-muted"
          />
        </Field>
      </div>
    </SettingsSection>
  );
};
